import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import { isPasswordValid } from '../../utils/passwordPolicy';

export default function PasswordForm({ hasPassword, onSubmit, loading }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (hasPassword && !currentPassword) {
      setError('Current password is required');
      return;
    }
    if (!newPassword || !confirmPassword) {
      setError('Please fill in all password fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!isPasswordValid(newPassword)) {
      setError('Password does not meet all requirements');
      return;
    }
    if (hasPassword && currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    try {
      await onSubmit({
        currentPassword: hasPassword ? currentPassword : undefined,
        newPassword,
        confirmPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message || 'Failed to update password');
    }
  };

  const inputClass =
    'w-full rounded-lg border border-ga-border-subtle bg-ga-bg-page px-3 py-2 text-sm text-ga-text-primary focus:outline-none focus:ring-2 focus:ring-ga-primary/30';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {hasPassword && (
        <div>
          <label htmlFor="current-password" className="block text-xs font-medium text-ga-text-muted mb-1">
            Current password
          </label>
          <div className="relative">
            <input
              id="current-password"
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className={inputClass}
              autoComplete="current-password"
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-ga-text-muted hover:text-ga-text-primary"
              onClick={() => setShowCurrent((v) => !v)}
              aria-label={showCurrent ? 'Hide password' : 'Show password'}
            >
              {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
      )}

      <div>
        <label htmlFor="new-password" className="block text-xs font-medium text-ga-text-muted mb-1">
          {hasPassword ? 'New password' : 'Password'}
        </label>
        <div className="relative">
          <input
            id="new-password"
            type={showNew ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className={inputClass}
            autoComplete="new-password"
          />
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-ga-text-muted hover:text-ga-text-primary"
            onClick={() => setShowNew((v) => !v)}
            aria-label={showNew ? 'Hide password' : 'Show password'}
          >
            {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <div>
        <label htmlFor="confirm-password" className="block text-xs font-medium text-ga-text-muted mb-1">
          Confirm password
        </label>
        <div className="relative">
          <input
            id="confirm-password"
            type={showConfirm ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={inputClass}
            autoComplete="new-password"
          />
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-ga-text-muted hover:text-ga-text-primary"
            onClick={() => setShowConfirm((v) => !v)}
            aria-label={showConfirm ? 'Hide password' : 'Show password'}
          >
            {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {(newPassword || confirmPassword) && (
        <PasswordStrengthIndicator password={newPassword} />
      )}

      {error && (
        <p className="text-sm text-ga-danger" role="alert">{error}</p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="inline-flex items-center justify-center rounded-lg bg-ga-primary px-4 py-2 text-sm font-medium text-white hover:bg-ga-primary/90 disabled:opacity-50 min-h-10"
      >
        {loading ? 'Saving…' : hasPassword ? 'Change password' : 'Set password'}
      </button>
    </form>
  );
}
